import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '../services/api';
import { getStaticUrl } from '../constants/config';
import { registerNotificationCallback } from '../services/errorHandler';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [videoUploadModalOpen, setVideoUploadModalOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const openUploadModal = useCallback(() => setUploadModalOpen(true), []);
  const closeUploadModal = useCallback(() => setUploadModalOpen(false), []);
  const openVideoUploadModal = useCallback(() => setVideoUploadModalOpen(true), []);
  const closeVideoUploadModal = useCallback(() => setVideoUploadModalOpen(false), []);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  // Register notification callback for global error handler
  useEffect(() => {
    registerNotificationCallback(showNotification);
    return () => registerNotificationCallback(null);
  }, [showNotification]);

  // Transform backend user to frontend shape
  const transformUser = (user) => {
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    const name = user.name || (firstName && lastName ? `${firstName} ${lastName}` : user.name || '');

    // Compute initials
    let initials = user.initials || '';
    if (!initials) {
      if (firstName && lastName) {
        initials = (firstName[0] + lastName[0]).toUpperCase().slice(0, 2);
      } else if (name) {
        initials = name
          .split(' ')
          .map(n => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();
      }
    }

    return {
      ...user,
      name,
      firstName,
      lastName,
      avatar: getStaticUrl(user.thumbnail || user.avatar || user.avatar_url || ''),
      initials,
    };
  };

  // Initialize authentication state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token by fetching current user
          const response = await authAPI.getCurrentUser();
          const user = transformUser(response.data.data);

          setCurrentUser(user);
          setIsAuthenticated(true);
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(user));
        } catch (error) {
          console.error('Failed to validate token:', error);
          // Clear invalid token
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      const { token, user } = response.data.data;

      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update state
      setCurrentUser(user);
      setIsAuthenticated(true);

      showNotification(`Login successful as ${user.role}!`, 'success');
      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || 'Invalid email or password';
      showNotification(errorMessage, 'error');
      return { success: false, error: errorMessage };
    }
  }, [showNotification]);

  // Complete an NLNG SSO sign-in: exchange the CAS ticket (+ anti-forgery
  // state) for the same { token, user } payload as the password login.
  // Errors are returned (not toasted) so the SSO callback page can show them inline.
  const loginWithSso = useCallback(async (ticket, state) => {
    try {
      const response = await authAPI.ssoValidate(ticket, state);

      const { token, user } = response.data.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      setCurrentUser(user);
      setIsAuthenticated(true);

      showNotification(`Signed in with NLNG SSO as ${user.role}!`, 'success');
      return { success: true, user };
    } catch (error) {
      console.error('SSO login error:', error);
      const errorMessage = error.response?.data?.error || 'SSO sign-in failed. Please try again.';
      return { success: false, error: errorMessage };
    }
  }, [showNotification]);

  const logout = useCallback(async () => {
    try {
      // Call logout API to invalidate token on server (optional)
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with client-side cleanup even if API fails
    }

    // Clear client-side state
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('intendedRoute');
    setCurrentUser(null);
    setIsAuthenticated(false);

    showNotification('Logged out successfully', 'info');
  }, [showNotification]);

  return (
    <AppContext.Provider
      value={{
        sidebarCollapsed,
        toggleSidebar,
        currentUser,
        setCurrentUser,
        isAuthenticated,
        login,
        loginWithSso,
        logout,
        uploadModalOpen,
        openUploadModal,
        closeUploadModal,
        videoUploadModalOpen,
        openVideoUploadModal,
        closeVideoUploadModal,
        notification,
        showNotification,
        loading,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};
